/**
 * Spanish (source-of-truth) UI copy for Bow and Arrow.
 *
 * Sacred-name discipline (see CLAUDE.md and docs/public-chat-architecture.md §8):
 * the names Yahushua, Yahweh, Padre, and Todopoderoso always appear in their
 * canonical Spanish form. They are NEVER translated to Jesus / the Lord /
 * Jehovah / Father in the English table either — strings.en.ts mirrors
 * these sacred-name strings exactly.
 */
import type { Strings } from "./types";

export const stringsEs: Strings = {
  appTitle: "Arco y Flecha",
  appTagline: "Preguntas sobre las enseñanzas de Koresh, con citas a la fuente.",

  idleKicker: "Una herramienta de estudio bíblico",
  idleTitle: "Preguntad, y se os responderá.",
  idleSub:
    "Las enseñanzas de Koresh, reunidas para leer y para responder tus preguntas.",
  noticeIndexingLead: "La biblioteca sigue creciendo",
  noticeIndexingBody:
    "Añadimos las enseñanzas de Koresh continuamente. Si una respuesta llega incompleta o una fuente aún no aparece, vuelve a preguntar en los próximos días.",
  noticeDismissAria: "Cerrar aviso",
  noticeLatestLabel: "Recién añadido",
  noticeLatestChapters: (n: number): string => (n === 1 ? "1 capítulo" : `${n} capítulos`),
  noticeLatestAria: (book: string): string => `Leer ${book} en la biblioteca`,

  composerPlaceholder: "Haz tu pregunta sobre las enseñanzas de Koresh…",
  composerPlaceholderDock: "Haz otra pregunta…",
  composerSubmit: "Soltar",
  composerSubmitting: "Buscando…",
  composerHintTagline: "Cada pregunta es una flecha que extiende Su Palabra por todo el mundo",
  composerHintEnter: "Enviar",
  composerCharCount: (n: number, max: number): string => `${n}/${max}`,

  threadUserLabel: "Tú",
  threadAssistantLabel: "Arco y Flecha",
  threadSourcesLabel: "Fuentes",
  threadTimestamp: (epochMs: number): string => {
    const d = new Date(epochMs);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
      ? d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  },
  threadStreamingLabel: "Redactando respuesta…",
  threadRecheckingLabel: "Revisando las fuentes con más cuidado…",
  answerIncomplete: "La respuesta se interrumpió. Intenta de nuevo.",
  composerStop: "Detener",
  composerStopAria: "Detener la respuesta",
  answerStopped: "Respuesta detenida.",
  rewriteInterpretedAs: (standaloneQuery: string): string =>
    `Interpreté tu pregunta como: «${standaloneQuery}»`,
  rewriteUseExactWords: "Ver respuesta literal",
  rewriteNotIt: "Si no era eso, escríbela completa.",
  acknowledgeReply: (amen: boolean): string =>
    `${amen ? "Amén. " : ""}Con gusto. Cuando quieras, pregúntame otra cosa sobre las enseñanzas de Koresh.`,
  threadNewChat: "Nueva",

  navNewChat: "Nueva conversación",
  navAbout: "Acerca de",
  navDevelopedBy: "Desarrollado por GreenScript",
  navOfficialSite: "Congregación: El Camino de YHWH",
  navBiblioteca: "Biblioteca",
  corpusKicker: "Biblioteca del estudio",
  corpusTitle: "El corpus, en orden",
  corpusSubtitle: "Lee las enseñanzas de Koresh en orden bíblico, el texto limpio que fundamenta cada respuesta.",
  corpusBackToChat: "Volver al chat",
  corpusOpenLibrary: "Abrir la biblioteca",
  corpusChaptersLabel: "Capítulos",
  corpusChapterLabel: "Capítulo",
  corpusChapterCount: (n: number): string => (n === 1 ? "1 capítulo" : `${n} capítulos`),
  corpusPrevChapter: "Anterior",
  corpusNextChapter: "Siguiente",
  corpusPageNavLabel: "Capítulos contiguos",
  corpusNotIndexedNotice:
    "Este capítulo aún no está en el índice del chat, al preguntar, la respuesta puede no citar esta sección.",
  corpusShortNotIndexedNotice:
    "Esta copia de lectura es independiente del índice activo. La disponibilidad de esta Flecha en el chat se verifica al preguntar.",
  corpusShortNotFound: "Esta Flecha no existe o ya no está disponible en la biblioteca.",
  corpusShortError: "No se pudo cargar esta Flecha. Vuelve al índice e inténtalo de nuevo.",
  navConexiones: "Conexiones",
  connectionsKicker: "Mapa del estudio",
  connectionsTitle: "Conexiones de la Escritura",
  connectionsSubtitle:
    "Las conexiones entre pasajes según la enseñanza de Koresh, derivadas del corpus y revisadas a mano.",
  connectionsSearchPlaceholder: "Busca un pasaje (p. ej. Apocalipsis 6)…",
  connectionsSuggestions: "Pasajes más conectados",
  connectionsLaneReviewed: "Revisado",
  connectionsLaneDerived: "Derivado del corpus (automático)",
  connectionsEvidence: "Evidencia en el corpus",
  connectionsOccurrences: (n: number): string => (n === 1 ? "1 mención en el corpus" : `${n} menciones en el corpus`),
  connectionsOpenReader: "Ver en la biblioteca",
  connectionsAskConnection: "Preguntar sobre esta conexión",
  connectionsCaminoHeading: "Recórrela como camino guiado",
  connectionsTruncated: "Mostrando las conexiones más fuertes, el vecindario completo es mayor.",
  connectionsLoading: "Cargando el mapa…",
  connectionsError: "No se pudo cargar el mapa. Genera el grafo con «npm run corpus:graph».",
  connectionsEmpty: "Aún no hay conexiones extraídas del corpus.",
  connectionsBack: "Volver a la búsqueda",
  connectionsStats: (edges: number, nodes: number): string =>
    `${edges} conexiones entre ${nodes} pasajes`,
  corpusPrismFilterLabel: "Capas de enseñanza",
  connectionsGroundingConnects: "Conexión afirmada por Koresh",
  connectionsGroundingMentions: "Solo co-mencionados (sin relación afirmada)",
  connectionsGroundingUnclear: "Relación no concluyente",
  corpusGlossaryLabel: "Glosario del corpus",
  navChat: "Chat",
  connectionsOnlyAffirmed: "Solo afirmadas",
  connectionsTrailLabel: "Recorrido",
  connectionsLegendAffirmed: "Afirmadas",
  connectionsLegendDerived: "Derivadas (sin juzgar)",
  connectionsLegendMentions: "Solo co-mencionadas",
  citationViewConnections: "Ver conexiones",
  corpusContinueReading: "Continúa leyendo",
  corpusSourceLabel: "Fuente",
  corpusWatchOnYouTube: "Ver en YouTube",
  corpusEmptyChapter: "Este capítulo aún no tiene texto limpio para mostrar.",
  corpusLoading: "Cargando…",
  corpusError: "No se pudo cargar la biblioteca. Genera el manifiesto con «npm run corpus:reader».",
  corpusReadabilitySize: "Tamaño del texto",
  corpusReadabilityLarger: "Aumentar el texto",
  corpusReadabilitySmaller: "Reducir el texto",
  corpusReadabilityType: "Cambiar tipografía",
  corpusBookListLabel: "Libros",
  corpusAskAbout: "Preguntar sobre esto",
  corpusAskPrompt: (book: string, chapter: number, section: string | null): string =>
    section !== null
      ? `Explícame esta sección de ${book} ${chapter}: «${section}».`
      : `Explícame esta sección de ${book} ${chapter}.`,

  navCaminos: "Caminos",
  caminosKicker: "Caminos de luz",
  caminosTitle: "Todos los caminos llevan a EL camino",
  caminosSubtitle:
    "Recorre caminos guiados de estudio, la cámara vuela de pasaje en pasaje, y en cada parada se muestra la enseñanza real de Koresh, una manera más dinámica de observar y recorrer las conexiones de la escritura.",
  caminosAffirmedNote: "Cada salto recorre una conexión afirmada por Koresh, ningún camino vuela sin evidencia.",
  caminosCardStops: (n: number): string => (n === 1 ? "1 parada" : `${n} paradas`),
  caminosCardMinutes: (n: number): string => `~${n} min`,
  caminosBegin: "Comenzar el camino",
  caminosStopOf: (current: number, total: number): string => `Parada ${current} de ${total}`,
  caminosConnectedWith: (label: string): string => `Conexión desde ${label}`,
  caminosTaughtIn: (label: string): string => `Enseñado al estudiar ${label}`,
  caminosNext: "Siguiente parada",
  caminosPrev: "Parada anterior",
  caminosExit: "Salir",
  caminosKeysHint: "← → navegan · Esc sale",
  caminosProgressLabel: "Paradas del camino",
  caminosLoading: "Cargando los caminos…",
  caminosError: "No se pudieron cargar los caminos. Genera el artefacto con «npm run caminos:compile».",
  caminosEmpty: "Aún no hay caminos promovidos.",

  // El Hilo Escarlata — traced flights (Task 226).
  caminosTraceTitle: "Traza tu propio camino",
  caminosTraceHint: "Elige dos pasajes y vuela la conexión afirmada entre ellos.",
  caminosTraceFrom: "Desde",
  caminosTraceTo: "Hasta",
  caminosTraceSearchPlaceholder: "Busca un pasaje…",
  caminosTraceGo: "Trazar",
  caminosTraceBadge: "ruta calculada",
  caminosThemeBadge: "tema",
  caminosExploreMap: "Explorar en el mapa",
  caminosTraceReadFull: "Lee la enseñanza completa en la biblioteca ↗",
  caminosTraceMissTitle: "Sin ruta afirmada",
  caminosTraceMiss: (from: string, to: string): string =>
    `El corpus aún no conecta ${from} y ${to} por enseñanzas afirmadas de Koresh.`,
  caminosTraceAsk: "Pregúntalo en el chat",
  caminosTraceBack: "Volver a Caminos",

  corpusBookmarkMenuLabel: "Guardar punto de lectura",
  corpusBookmarkSave: "Guardar punto de lectura",
  corpusBookmarkSaved: "Punto de lectura guardado",
  corpusBookmarkPinLabel: "Tu punto de lectura",
  corpusBookmarkJump: "Tu punto de lectura",
  corpusBookmarkJumpAria: (book: string, chapter: number): string =>
    `Volver a tu punto de lectura en ${book} ${chapter}`,
  corpusBookmarkClear: "Quitar",

  copy: "Copiar",
  copyDone: "Copiado",
  copyFailed: "Error al copiar",
  shareLabel: "Compartir",
  shareCopied: "Enlace copiado",
  feedbackGroupLabel: "¿Te sirvió esta respuesta?",
  feedbackUseful: "Útil",
  feedbackNotUseful: "No útil",
  feedbackReasonsLegend: "¿Por qué no te sirvió?",
  feedbackReasonIncorrect: "Enseñanza incorrecta",
  feedbackReasonMissing: "Falta enseñanza",
  feedbackReasonUnrelated: "Fuente no relacionada",
  feedbackReasonSlow: "Tardó demasiado",
  feedbackNoteLabel: "Nota (opcional)",
  feedbackNoteHint: "No incluyas datos personales.",
  feedbackShareLabel: "Incluir mi pregunta y la respuesta para que el equipo pueda revisarlas.",
  feedbackSend: "Enviar",
  feedbackCancel: "Cancelar",
  feedbackThanks: "Todá por tu opinión.",
  feedbackFailed: "No se pudo enviar. Intenta de nuevo.",
  feedbackRateLimited: "Demasiados envíos seguidos. Intenta en un momento.",
  sharedQuestionBadge: "Pregunta compartida",

  aboutTitle: "Acerca de Arco y Flecha",
  aboutP1:
    "Arco y Flecha es una interfaz de chat para el estudio de la Biblia según las enseñanzas de Koresh.",
  aboutP2:
    "Las respuestas se basan en el corpus indexado de esas enseñanzas, no es un chatbot bíblico general.",
  aboutP3:
    "Revisa siempre las fuentes citadas. Cuando el corpus no tiene evidencia suficiente, la herramienta lo dice con claridad en lugar de inventar.",
  aboutPrivacy:
    "Para mejorar las respuestas, guardamos durante 30 días el texto de las preguntas que la herramienta no pudo responder a la primera, sin ningún dato de quién las hizo.",
  aboutClose: "Cerrar",

  holdIdle: "Mantén presionado para soltar la flecha",
  holdCharging: "Suelta para enviar",

  sourceRailSubtitle: "Última respuesta",
  sourceRailEmpty: "Esta respuesta no citó fuentes del corpus.",
  sourceRailLoading: "Preparando fuentes",
  inlineSourcesToggle: (count: number): string => `Fuentes (${count})`,

  refusalHelpLabel: "Qué puedes hacer",
  refusalBookLead: (passage: string): string =>
    `${passage} está en la biblioteca: ábrelo para leerlo, o pregunta por un capítulo.`,
  refusalChapterLead: "Prueba a preguntar por todo el capítulo, o léelo en la biblioteca.",
  refusalChapterMissing: (passage: string): string => `${passage} todavía no está en la biblioteca.`,
  refusalBookMissing: (book: string): string => `${book} todavía no está en la biblioteca.`,
  refusalHomonym: (book: string): string => `La biblioteca tiene ${book}, que es otro libro.`,
  refusalGeneralLead:
    "Prueba con otras palabras, o nombra el libro y el capítulo (por ejemplo, «Éxodo 12»).",
  refusalTaskLead:
    "Arco y Flecha responde con lo que Koresh ha enseñado en sus videos; no hace análisis ni traducciones nuevas. Prueba con una pregunta corta sobre su enseñanza.",
  refusalOpenBook: (passage: string): string => `Abrir ${passage} en la biblioteca`,
  refusalBrowseLibrary: "Ver los libros de la biblioteca",
  readingLinkBook: (book: string): string => `Leer ${book} capítulo por capítulo en la biblioteca`,
  readingLinkChapter: (passage: string): string => `Leer ${passage} en la biblioteca`,

  errorRateLimit: (seconds: number): string =>
    `Demasiadas solicitudes. Intenta de nuevo en ${seconds} segundos.`,
  errorQueryTooLong: (max: number): string =>
    `La consulta excede el límite de ${max} caracteres.`,
  errorTooManySources: "Demasiadas fuentes solicitadas.",
  errorNetwork: "Error de red. Intenta de nuevo.",
  errorUnknown: "Ocurrió un error inesperado. Intenta de nuevo.",
  errorServiceUnavailable:
    "El servicio no está disponible en este momento. Por favor, intenta de nuevo en unos minutos.",
  errorRetry: "Reintentar",
  errorDismiss: "Cerrar",

  citationOpenLabel: "Abrir en YouTube",
  citationOpenInReader: "Ver en biblioteca",
  citationTimestampSeparator: "–",
  citationBookChapter: (book: string, chapter: number): string =>
    `${capitalize(book)} ${chapter}`,
  citationNoTimestamp: "Sin marca de tiempo",

  relatedLabel: "Pasajes conectados",
  relatedLead: "Conexiones del pasaje consultado:",
  relatedQuestion: (from: string | null, to: string): string =>
    from === null
      ? `¿Qué enseña Koresh sobre ${to}?`
      : `¿Cómo conecta Koresh ${from} con ${to}?`,

  arrowLoadingLabel: "Bebiendo del texto",
  arrowLoadingLabelReduced: "Cargando respuesta",

  langToggleLabel: "Idioma",
  langOptionEs: "Español",
  langOptionEn: "English",

  // Sacred-name strings. Same in both language tables. Never translated.
  sacredYahushua: "Yahushua",
  sacredYahweh: "Yahweh",
  sacredPadre: "Padre",
  sacredTodopoderoso: "Todopoderoso",
};

function capitalize(value: string): string {
  if (value.length === 0) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
